/**
 * @fileoverview The main spinner component that renders the visual wheel or bottle spinner.
 *
 * This component handles multiple spinner types (bottle, wheel, dice) and renders
 * them with appropriate animations, winner highlighting, and interactive elements.
 * It includes sub-components for dice rendering and winner label animations.
 */

import React, { useState } from "react";
import styles from "./Spinner.module.css";
import CapsulePill from './CapsulePill';
import { colorOptions, colorSkinOptions, colorEyesOptions, colorHairOptions } from '../../lib/SpinnerContext';
import { useTranslation } from 'react-i18next';
import { APP_PROPERTIES } from '../../config/appProperties';

/**
 * Props for the main Spinner component.
 */
interface SpinnerProps {
  /** The current rotation angle of the spinner in degrees. */
  angle: number;
  /** Array of participant names or values to display on the spinner. */
  participants: string[];
  /** URL of the bottle image for bottle-type spinners. */
  bottleImg: string;
  /** Callback function triggered when the spinner is clicked or activated. */
  onSpin?: () => void;
  /** Flag indicating if the spinner is currently spinning. */
  spinning?: boolean;
  /** Index of the winning participant, used for highlighting. */
  winnerIndex?: number | null;
  /** The dimension (width/height) of the spinner container. */
  containerDimension: number;
  /** Type of spinner to render ('bottle', 'wheel', etc.). */
  spinnerType?: string;
  /** Color type for dynamic color selection */
  colorType?: 'random' | 'skin' | 'eyes';
}

/**
 * Props for the DiceImage sub-component.
 */
interface DiceImageProps {
  /** The dice value (1-6). */
  value: number;
  /** X coordinate for positioning the dice. */
  x: number;
  /** Y coordinate for positioning the dice. */
  y: number;
  /** Size of the dice in pixels. */
  size: number;
  /** Rotation angle of the dice. */
  rotation: number;
  /** Flag indicating if this dice is the winner. */
  isWinner: boolean;
}

/**
 * Renders a single dice with dots representing its value (1-6).
 * Includes special styling for the winning dice with golden glow effects.
 * @param {DiceImageProps} props - The props for the dice component.
 * @returns {JSX.Element} The rendered dice component.
 */
const DiceImage = React.memo(({ value, x, y, size, rotation, isWinner }: DiceImageProps) => {
  // Define the dot positions for each dice value (1-6)
  const dotPositions: Record<number, number[][]> = {
    1: [[50, 50]],
    2: [[25, 25], [75, 75]],
    3: [[25, 25], [50, 50], [75, 75]],
    4: [[25, 25], [25, 75], [75, 25], [75, 75]],
    5: [[25, 25], [25, 75], [50, 50], [75, 25], [75, 75]],
    6: [[25, 25], [25, 50], [25, 75], [75, 25], [75, 50], [75, 75]],
  };

  const isSix = value === 6;
  const dotColor = isSix ? 'red' : '#111';

  // Animation container with fixed position and scale
  const containerClasses = isWinner ? 'dice-winner' : '';

  // Store initial styles
  const diceStyle: React.CSSProperties = {
    position: 'absolute' as const,
    left: `${x}px`,
    top: `${y}px`,
    width: `${size}px`,
    height: `${size}px`,
    borderRadius: `${size * 0.18}px`,
    display: 'flex' as const,
    alignItems: 'center' as const,
    justifyContent: 'center' as const,
    pointerEvents: 'none' as const,
    transition: 'all 0.3s', // For properties not handled by CSS animation
    zIndex: isWinner ? 20 : 10, // Reduced z-index values
    transform: `translate(-50%, -50%) rotate(${rotation}deg)`,
    '--rotation': `${rotation}deg`,

    // Apply 3D glassy look with different styles for winner vs non-winner
    ...(isWinner
      ? { // Winner state: Glassy Gold
          background: 'linear-gradient(145deg, #fff0b3, #ffe066)', // Matched to winner pills
          border: '2px solid #ffd700',
          boxShadow: 'inset 1px 1px 3px rgba(255,255,255,0.6), inset -1px -1px 3px rgba(150,100,0,0.3), 0 0 16px 5px #ffd700, 0 0 0 3px rgba(255,255,255,0.8)',
        }
      : { // Non-winner state: Enhanced Glassy White
          background: 'linear-gradient(145deg, rgba(255,255,255,0.95), rgba(225,225,225,0.7))',
          border: '1px solid rgba(0,0,0,0.15)',
          boxShadow: 'inset 1.5px 1.5px 3px rgba(0,0,0,0.12), inset -1.5px -1.5px 3px rgba(255,255,255,0.8), 0 0 6px rgba(0,0,0,0.12)',
        }),
  };

  return (
    <div
      style={diceStyle as React.CSSProperties}
      className={containerClasses}
    >
      <svg width={size * 0.8} height={size * 0.8} viewBox="0 0 100 100">
        {dotPositions[value]?.map(([cx, cy], i) => (
          <circle 
            key={i} 
            cx={cx} 
            cy={cy} 
            r="8" 
            fill={dotColor}
          />
        ))}
      </svg>
    </div>
  );
});

/**
 * Renders a label for a participant with special animation for the winner.
 * Includes glow effects and font size animations for the winning participant.
 * @param {Object} props - The props for the winner label component.
 * @param {number} props.x - X coordinate for the label.
 * @param {number} props.y - Y coordinate for the label.
 * @param {number} props.midAngle - The angle for rotating the label.
 * @param {string} props.displayName - The text to display.
 * @param {boolean} props.isWinner - Whether this label represents the winner.
 * @param {boolean} props.isColor - Whether this is a color label.
 * @param {number} props.baseFontSize - The base font size for the label.
 * @param {Array} props.colorOptions - Available color options for translation.
 * @param {string} props.name - The original name value.
 * @returns {JSX.Element} The rendered winner label component.
 */
const WinnerLabel = React.memo(({
  x, y, midAngle, displayName, isWinner, isColor, baseFontSize, colorOptions, name, currentColorOptions, participantIndex, colorType
}: {
  x: number;
  y: number;
  midAngle: number;
  displayName: string;
  isWinner: boolean;
  isColor: boolean;
  baseFontSize: number;
  colorOptions: Array<{ name: string; label: string; key: string }>;
  name: string;
  currentColorOptions: Array<{ name: string; label: string; key: string }>;
  participantIndex: number;
  colorType: 'random' | 'skin' | 'eyes' | 'hair';
}) => {
  const [winnerFontSize, setWinnerFontSize] = React.useState(baseFontSize);
  const { t } = useTranslation();

  /**
   * Calculate the optimal contrasting text color based on background color
   * @param {string} backgroundColor - The hex color of the background
   * @returns {string} The optimal text color ('white' or 'black')
   */
  const getContrastingTextColor = (backgroundColor: string): string => {
    // Handle non-hex colors by returning default
    if (!/^#([0-9A-Fa-f]{6}|[0-9A-Fa-f]{3})$/.test(backgroundColor)) {
      return '#000000'; // Default to black for non-hex colors
    }

    // Convert hex to RGB
    let hex = backgroundColor.replace('#', '');
    if (hex.length === 3) {
      hex = hex.split('').map(char => char + char).join('');
    }
    
    const r = parseInt(hex.substr(0, 2), 16);
    const g = parseInt(hex.substr(2, 2), 16);
    const b = parseInt(hex.substr(4, 2), 16);

    // Calculate relative luminance using the standard formula
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;

    // Return white text for dark backgrounds, black text for light backgrounds
    return luminance > 0.5 ? '#000000' : '#ffffff';
  };

  // Animate the font size for the winner with a pulsing effect
  React.useEffect(() => {
    let raf: number;
    let start: number | null = null;
    if (isWinner) {
      const duration = 2350; // Slower pulse (3 seconds)
      const animate = (timestamp: number) => {
        if (!start) start = timestamp;
        const elapsed = (timestamp - start) % duration;
        // Animate between baseFontSize and baseFontSize * 1.5
        const tVal = Math.abs(Math.sin((Math.PI * elapsed) / duration));
        setWinnerFontSize(baseFontSize * (1 + tVal * 0.5)); // 1x to 1.5x
        raf = requestAnimationFrame(animate);
      };
      raf = requestAnimationFrame(animate);
      return () => {
        cancelAnimationFrame(raf);
        setWinnerFontSize(baseFontSize);
      };
    } else {
      setWinnerFontSize(baseFontSize);
    }
  }, [isWinner, baseFontSize]);

  // Get translated color name if isColor
  const colorObj = isColor ? currentColorOptions.find((c) => c.name === name) : undefined;
  let colorLabel = name.slice(0, APP_PROPERTIES.PARTICIPANT_NAME_CHAR_LIMIT);
  
  if (isColor && colorObj) {
    // Use translation system for color names
    let translationSection;
    switch (colorType) {
      case 'skin':
        translationSection = 'skinColors';
        break;
      case 'eyes':
        translationSection = 'eyeColors';
        break;
      case 'hair':
        translationSection = 'hairColors';
        break;
      default:
        translationSection = 'colors';
    }
    colorLabel = t(`${translationSection}.${colorObj.key}`);
  }

  // Determine the background color for this segment
  const backgroundColor = isColor ? name : currentColorOptions[participantIndex % currentColorOptions.length]?.name || '#ccc';
  
  // Calculate the optimal text color based on background
  const textColor = getContrastingTextColor(backgroundColor);

  return (
    <g>
      {isWinner && (
        <>
          {/* Outer, lighter, wider glow */}
          <text
            x={x}
            y={y}
            textAnchor="middle"
            alignmentBaseline="middle"
            fontSize={winnerFontSize}
            fontWeight="bold"
            fill="none"
            stroke="#fff700"
            strokeWidth={winnerFontSize * 0.7}
            style={{
              userSelect: 'none',
              dominantBaseline: 'middle',
              opacity: 0.5,
              filter: 'blur(12px)'
            }}
            transform={`rotate(${midAngle}, ${x}, ${y})`}
          >
            {colorLabel}
          </text>
          {/* Inner, stronger gold glow */}
          <text
            x={x}
            y={y}
            textAnchor="middle"
            alignmentBaseline="middle"
            fontSize={winnerFontSize}
            fontWeight="bold"
            fill="none"
            stroke="#ffd700"
            strokeWidth={winnerFontSize * 0.4}
            style={{
              userSelect: 'none',
              dominantBaseline: 'middle',
              opacity: 0.9,
              filter: 'blur(6px)'
            }}
            transform={`rotate(${midAngle}, ${x}, ${y})`}
          >
            {colorLabel}
          </text>
        </>
      )}
      <text
        x={x}
        y={y}
        textAnchor="middle"
        alignmentBaseline="middle"
        fontSize={isWinner ? winnerFontSize : baseFontSize}
        fontWeight="bold"
        fill={textColor}
        style={{
          userSelect: 'none',
          dominantBaseline: 'middle'
        }}
        transform={`rotate(${midAngle}, ${x}, ${y})`}
      >
        {colorLabel}
      </text>
    </g>
  );
});

/**
 * The main Spinner component that renders different types of spinners.
 * Supports bottle spinners, wheel spinners, and dice spinners with
 * appropriate animations and winner highlighting.
 * @param {SpinnerProps} props - The props for the spinner component.
 * @returns {JSX.Element} The rendered spinner component.
 */
const Spinner: React.FC<SpinnerProps> = ({
  angle,
  participants,
  bottleImg,
  onSpin,
  spinning,
  winnerIndex,
  containerDimension,
  spinnerType = 'bottle',
  colorType = 'random',
}) => {
  const { t } = useTranslation();

  // Keep original angle for smooth continuous rotation
  // The jerky animation issue is better solved with CSS optimizations

  // Determine the correct color options based on colorType
  const currentColorOptions = colorType === 'skin' ? colorSkinOptions : colorType === 'eyes' ? colorEyesOptions : colorType === 'hair' ? colorHairOptions : colorOptions;

  // Spin hint state: show only before first spin
  const [showSpinHint, setShowSpinHint] = useState(true);

  // Directly use the containerDimension prop as the base size
  const size = containerDimension;

  // Proportional sizing based on the 'size' (derived from containerDimension)
  const center = size / 2;
  const greyCircleActualRadius = size * 0.425; // 85% diameter, so radius is 42.5%
  const bottleActualSize = size * 0.7;
  const pillFontSize = Math.max(10, size * 0.04); // e.g., 4% of container size, min 10px

  // Add dice unicode and helper
  const diceUnicode = ['⚀', '⚁', '⚂', '⚃', '⚄', '⚅'];
  const isDiceMode = participants.every(name => diceUnicode.includes(name));
  const isNumbersMode = participants.length > 0 && participants.every(name => /^-?\d+$/.test(name));

  // Responsive font size for hint
  const hintFontSize = Math.max(18, size * 0.08); // 8% of spinner, min 18px
  const hintRadius = greyCircleActualRadius * 1.08; // Slightly outside the bottle

  /**
   * Handles spin interactions (click or keyboard).
   * Hides the spin hint after the first spin.
   */
  const handleSpin = (e?: React.MouseEvent | React.KeyboardEvent) => {
    if (showSpinHint) setShowSpinHint(false);
    if (onSpin) onSpin();
  };

  // Keyboard shortcut (Ctrl+Enter) for spinning
  React.useEffect(() => {
    if (!showSpinHint) return;
    const handler = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
        setShowSpinHint(false);
        if (onSpin) onSpin();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [showSpinHint, onSpin]);

  if (size === 0) {
    // Render nothing or a minimal placeholder if containerDimension is 0
    return <div className={styles.spinnerContainer} style={{width: '100%', height: '100%', visibility: 'hidden'}}></div>; 
  }

  // Wheel mode: render only the wheel and pointer, no pills/dice/grey circle
  if (spinnerType === 'wheel') {
    const wheelPadding = size * 0.03; // 3% padding
    const wheelRadius = (size / 2) - wheelPadding;
    const center = size / 2;
    return (
      <div
        className={styles.spinnerContainer}
        style={{ position: 'relative', width: '100%', height: '100%' }}
      >
        <div
          style={{
            position: 'absolute',
            left: '50%',
            top: '50%',
            width: size,
            height: size,
            transform: 'translate(-50%, -50%)',
            pointerEvents: 'none',
          }}
        >
          {/* Triangle pointer for wheel mode */}
          {(() => {
            const triHeight = (size / 18.75) * 1.5; // 150% larger
            const triSide = (2 / Math.sqrt(3)) * triHeight;
            const gap = 0; // No gap, tip touches wheel perimeter
            const tipX = center + wheelRadius + gap;
            return (
              <svg
                width={triHeight}
                height={triSide}
                viewBox={`0 0 ${triHeight} ${triSide}`}
                style={{
                  position: 'absolute',
                  left: `${tipX - triHeight / 2 - 1}px`,
                  top: `${center - triSide / 2}px`,
                  zIndex: 20,
                  pointerEvents: 'none',
                  filter: 'drop-shadow(0 12px 12px rgba(0,0,0,0.25))', // bottom shadow only
                }}
              >
                <polygon
                  points={`0,${triSide / 2} ${triHeight},0 ${triHeight},${triSide}`}
                  fill="#eee"
                  stroke="#111"
                  strokeWidth={1}
                  style={{ filter: 'drop-shadow(0 12px 12px rgba(0,0,0,0.25))' }}
                />
              </svg>
            );
          })()}
          {/* Wheel SVG with pie segments */}
          <svg
            width={size}
            height={size}
            style={{
              position: 'absolute',
              left: 0,
              top: 0,
              transform: `rotate(${angle}deg)`,
              transition: spinning ? 'none' : 'transform 1s cubic-bezier(.17,.67,.83,.67)',
              willChange: 'transform',
              overflow: 'visible',
              transformOrigin: 'center center',
              borderRadius: '50%',
              // Apply bottle's smooth rotation optimizations
              backfaceVisibility: 'hidden',
              WebkitBackfaceVisibility: 'hidden',
              WebkitTransform: `rotate(${angle}deg) translateZ(0)`,
              WebkitTransition: spinning ? 'none' : 'transform 1s cubic-bezier(.17,.67,.83,.67)',
              WebkitWillChange: 'transform',
              transformStyle: 'preserve-3d',
              perspective: '1000px'
            }}
          >
            <defs>
              <filter id="wheelShadow" x="-20%" y="-20%" width="140%" height="140%">
                <feDropShadow dx="0" dy="2" stdDeviation="6" floodColor="rgba(0,0,0,0.18)" />
              </filter>
            </defs>
            <g filter="url(#wheelShadow)">
              {/* Wheel pie segments - each participant gets a slice */}
              {participants.map((name, i) => {
                const angle = 360 / participants.length;
                const startAngle = angle * i - 90;
                const endAngle = startAngle + angle;
                const largeArc = angle > 180 ? 1 : 0;
                const x1 = center + wheelRadius * Math.cos((Math.PI * startAngle) / 180);
                const y1 = center + wheelRadius * Math.sin((Math.PI * startAngle) / 180);
                const x2 = center + wheelRadius * Math.cos((Math.PI * endAngle) / 180);
                const y2 = center + wheelRadius * Math.sin((Math.PI * endAngle) / 180);
                const isColor = /^#([0-9A-Fa-f]{6}|[0-9A-Fa-f]{3})$/.test(name);
                return (
                  <path
                    key={i}
                    d={`M${center},${center} L${x1},${y1} A${wheelRadius},${wheelRadius} 0 ${largeArc} 1 ${x2},${y2} Z`}
                    fill={isColor ? name : currentColorOptions[i % currentColorOptions.length]?.name || '#ccc'}
                    stroke="#fff"
                    strokeWidth={3}
                  />
                );
              })}
            </g>
            {/* Text labels for non-dice mode */}
            {!isDiceMode && participants.map((name, i) => {
              const angle = 360 / participants.length;
              const midAngle = angle * i + angle / 2 - 90;
              const r = wheelRadius * 0.6;
              const x = center + r * Math.cos((Math.PI * midAngle) / 180);
              const y = center + r * Math.sin((Math.PI * midAngle) / 180);
              const isWinner = winnerIndex === i && !spinning;
              const displayName = name.length > 12 ? name.substring(0, 12) : name;
              const isColor = /^#([0-9A-Fa-f]{6}|[0-9A-Fa-f]{3})$/.test(name);
              const baseFontSize = Math.max(18, size * 0.045);
              return (
                <WinnerLabel
                  key={`${name}-${i}`}
                  x={x}
                  y={y}
                  midAngle={midAngle}
                  displayName={displayName}
                  isWinner={isWinner}
                  isColor={isColor}
                  baseFontSize={baseFontSize}
                  colorOptions={currentColorOptions}
                  name={name}
                  currentColorOptions={currentColorOptions}
                  participantIndex={i}
                  colorType={colorType}
                />
              );
            })}
            {/* Dice mode: render dice on the wheel */}
            {isDiceMode && (
              <g style={{ pointerEvents: 'none' }}>
                {participants.map((name, i) => {
                  const angle = 360 / participants.length;
                  const midAngle = angle * i + angle / 2 - 90;
                  const r = wheelRadius * 0.6;
                  const x = center + r * Math.cos((Math.PI * midAngle) / 180);
                  const y = center + r * Math.sin((Math.PI * midAngle) / 180);
                  const diceValue = ['⚀','⚁','⚂','⚃','⚄','⚅'].indexOf(name) + 1;
                  const isWinner = winnerIndex === i && !spinning;

                  return (
                    <foreignObject 
                      key={i}
                      x={x - size * 0.06}
                      y={y - size * 0.06}
                      width={size * 0.12}
                      height={size * 0.12}
                      style={{
                        overflow: 'visible',
                        transform: `rotate(${midAngle}deg)`,
                        transformOrigin: `${x}px ${y}px`,
                      }}
                    >
                      <div 
                        style={{ 
                          width: '100%', 
                          height: '100%', 
                          transform: `rotate(${-midAngle}deg)`,
                          transformOrigin: 'center center',
                        }}
                      >
                        <DiceImage
                          value={diceValue}
                          x={size * 0.06}
                          y={size * 0.06}
                          size={size * 0.12}
                          rotation={0}
                          isWinner={isWinner}
                        />
                      </div>
                    </foreignObject>
                  );
                })}
              </g>
            )}
          </svg>
          {/* Clickable overlay for spinning the wheel */}
          <button
            style={{
              position: 'absolute',
              left: 0,
              top: 0,
              width: size,
              height: size,
              background: 'transparent',
              border: 'none',
              cursor: !spinning ? 'pointer' : 'default',
              zIndex: 30,
              pointerEvents: !spinning ? 'auto' : 'none',
            }}
            onClick={!spinning ? onSpin : undefined}
            aria-label="Spin the wheel"
            disabled={spinning}
          />
        </div>
      </div>
    );
  }

  // Non-wheel mode: render grey circle, pills, dice, bottle
  return (
    <div 
      data-testid="spinner-container"
      className={styles.spinnerContainer}
      onClick={handleSpin}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleSpin(e);
        }
      }}
      role="button"
      tabIndex={0}
      style={{
        width: size,
        height: size,
        position: 'relative',
        cursor: 'pointer',
        userSelect: 'none',
        WebkitUserDrag: 'none',
        zIndex: 1 // Base z-index for spinner container
      }}
    >
      <div
        style={{
          position: 'absolute',
          left: '50%',
          top: '50%',
          width: greyCircleActualRadius * 2,
          height: greyCircleActualRadius * 2,
          transform: 'translate(-50%, -50%)',
          pointerEvents: 'none',
        }}
      >
        {/* Spin Hint Overlay - Curved text with iOS compatibility */}
        {showSpinHint && !spinning && !isDiceMode && (
          <svg
            width={greyCircleActualRadius * 2}
            height={greyCircleActualRadius * 2}
            style={{
              position: 'absolute',
              left: '50%',
              top: '50%',
              transform: 'translate(-50%, -50%) rotate(' + angle + 'deg)',
              pointerEvents: 'none',
              zIndex: 50,
            }}
          >
            <defs>
              {/* Cross/perpendicular arc segments for textPath, close to bottle */}
              {(() => {
                const degToRad = (deg: number): number => (deg * Math.PI) / 180;
                const pointOnCircle = (cx: number, cy: number, r: number, angleDeg: number): [number, number] => [
                  cx + r * Math.cos(degToRad(angleDeg)),
                  cy + r * Math.sin(degToRad(angleDeg)),
                ];
                const pillsCenter = greyCircleActualRadius;
                const textRadius = greyCircleActualRadius * 0.7;
                // Left arc: 135° to 225° (sweep flag 1)
                const [xL1, yL1] = pointOnCircle(pillsCenter, pillsCenter, textRadius, 128);
                const [xL2, yL2] = pointOnCircle(pillsCenter, pillsCenter, textRadius, 230);
                // Right arc: 315° to 45° (sweep flag 1)
                const [xR1, yR1] = pointOnCircle(pillsCenter, pillsCenter, textRadius, 305);
                const [xR2, yR2] = pointOnCircle(pillsCenter, pillsCenter, textRadius, 50);
                return <>
                  <path
                    id="spinHintPathLeft"
                    d={`M ${xL1},${yL1} A ${textRadius},${textRadius} 0 0 1 ${xL2},${yL2}`}
                  />
                  <path
                    id="spinHintPathRight"
                    d={`M ${xR1},${yR1} A ${textRadius},${textRadius} 0 0 1 ${xR2},${yR2}`}
                  />
                </>;
              })()}
              <filter id="spinHintShadow" x="-50%" y="-50%" width="200%" height="200%">
                <feDropShadow dx="0" dy="2" stdDeviation="4" floodColor="#444" floodOpacity="0.7" />
              </filter>
            </defs>
            {/* Left side: Click Here, curved along arc */}
            <text
              fontSize={hintFontSize}
              fontWeight="bold"
              fill="#fff"
              filter="url(#spinHintShadow)"
              letterSpacing="2"
              textAnchor="middle"
              dominantBaseline="middle"
              style={{
                fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
                WebkitFontSmoothing: 'antialiased',
                MozOsxFontSmoothing: 'grayscale',
              }}
            >
              <textPath
                xlinkHref="#spinHintPathLeft"
                startOffset="50%"
                alignmentBaseline="middle"
                textAnchor="middle"
                dominantBaseline="middle"
              >
                {t('spinner.hintClickHere', 'Click Here')}
              </textPath>
            </text>
            {/* Right side: Ctrl + Enter, curved along arc */}
            <text
              fontSize={hintFontSize}
              fontWeight="bold"
              fill="#fff"
              filter="url(#spinHintShadow)"
              letterSpacing="2"
              textAnchor="middle"
              dominantBaseline="middle"
              style={{
                fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
                WebkitFontSmoothing: 'antialiased',
                MozOsxFontSmoothing: 'grayscale',
              }}
            >
              <textPath
                xlinkHref="#spinHintPathRight"
                startOffset="50%"
                alignmentBaseline="middle"
                textAnchor="middle"
                dominantBaseline="middle"
              >
                {t('spinner.hintCtrlEnter', 'Ctrl + Enter')}
              </textPath>
            </text>
          </svg>
        )}
        {/* Grey circle - clickable only when not spinning */}
        <div
          className={styles.spinnerCircle}
          style={{
            width: '100%',
            height: '100%',
            left: 0,
            top: 0,
            borderRadius: '50%',
            cursor: !spinning ? 'pointer' : 'default',
            position: 'absolute',
            boxSizing: 'border-box',
            transform: 'none',
            pointerEvents: !spinning ? 'auto' : 'none',
          }}
          onClick={!spinning ? handleSpin : undefined}
        />
        {/* Spinner object (bottle) */}
        <img
          src={bottleImg}
          alt="Bottle"
          className={styles.spinnerBottle}
          loading="eager"
          style={{
            width: bottleActualSize,
            height: bottleActualSize,
            left: '50%',
            top: '50%',
            position: 'absolute',
            transform: `translate(-50%, -50%) rotate(${angle}deg)`,
            // Enhanced iOS optimizations for smoother rotation performance
            WebkitTransform: `translate(-50%, -50%) rotate(${angle}deg) translateZ(0)`,
            WebkitBackfaceVisibility: 'hidden',
            WebkitTransition: 'none', // Disable transitions during animation
            backfaceVisibility: 'hidden',
            willChange: 'transform',
            // Additional iOS performance optimizations
            WebkitUserDrag: 'none',
            WebkitTouchCallout: 'none',
            WebkitTapHighlightColor: 'transparent',
            // Hardware acceleration hints
            transformStyle: 'preserve-3d',
            perspective: '1000px',
          }}
          draggable={false}
        />
        {/* Pills, now using the grey circle's container for coordinates */}
        {!isDiceMode && participants.map((name, i) => {
          const pillsCenter = greyCircleActualRadius;
          const pillPlacementRadius = greyCircleActualRadius * 1.0;
          const theta = (2 * Math.PI * i) / participants.length - Math.PI / 2;
          const x = pillsCenter + pillPlacementRadius * Math.cos(theta);
          const y = pillsCenter + pillPlacementRadius * Math.sin(theta);
          const isWinner = winnerIndex === i && !spinning;
          const isColor = /^#([0-9A-Fa-f]{6}|[0-9A-Fa-f]{3})$/.test(name);
          const pillHeight = pillFontSize * 2.1 * 0.7;
          const colorObj = isColor ? currentColorOptions.find((c) => c.name === name) : undefined;
          const colorLabel = isColor && colorObj ? colorObj.label : name.slice(0, APP_PROPERTIES.PARTICIPANT_NAME_CHAR_LIMIT);
          
          // Determine pill color and text color based on type
          let pillColor = APP_PROPERTIES.PILLS_COLOR; // unified default color from app config
          let pillTextColor = APP_PROPERTIES.PILLS_TEXT_COLOR; // configurable text color
          if (isColor) {
            pillColor = name;
            pillTextColor = undefined; // Let CapsulePill calculate automatic contrast
          } else if (participants.length === 2 || (participants.length > 2 && new Set(participants).size === 2)) {
            // Binary mode - use alternating colors and text colors from app properties
            // Handles both 2 participants and multiple instances of 2 unique values
            const isEven = i % 2 === 0;
            pillColor = isEven ? APP_PROPERTIES.BINARY_PILLS_COLOR_EVEN : APP_PROPERTIES.BINARY_PILLS_COLOR_ODD;
            pillTextColor = isEven ? APP_PROPERTIES.BINARY_PILLS_TEXT_COLOR_EVEN : APP_PROPERTIES.BINARY_PILLS_TEXT_COLOR_ODD;
          }
          
          return (
            <div
              key={`${name}-${i}`}
              style={{
                left: x,
                top: y,
                position: 'absolute',
                transform: 'translate(-50%, -50%)',
                zIndex: isWinner ? 2 : 1,
                pointerEvents: 'none',
              }}
            >
              <CapsulePill
                color={pillColor}
                textColor={pillTextColor}
                width={undefined}
                height={pillHeight}
                isWinner={isWinner}
                colorType={isColor ? colorType : undefined}
                colorKey={isColor && colorObj ? colorObj.key : undefined}
              >
                {colorLabel}
              </CapsulePill>
            </div>
          );
        })}
        {/* Dice mode: render existing DiceImage-based dice centered on the same layer */}
        {isDiceMode && participants.map((name, i) => {
          const pillsCenter = greyCircleActualRadius;
          const dicePlacementRadius = greyCircleActualRadius * 1.0;
          const theta = (2 * Math.PI * i) / participants.length - Math.PI / 2;
          const x = pillsCenter + dicePlacementRadius * Math.cos(theta);
          const y = pillsCenter + dicePlacementRadius * Math.sin(theta);
          const diceValue = ['⚀','⚁','⚂','⚃','⚄','⚅'].indexOf(name) + 1;
          const isWinner = winnerIndex === i && !spinning;
          return (
            <DiceImage
              key={`dice-${name}-${i}`}
              value={diceValue}
              x={x}
              y={y}
              size={size * 0.12}
              rotation={0}
              isWinner={isWinner}
            />
          );
        })}
      </div>
    </div>
  );
};

export default Spinner; 